import type { GSplatRevealShowcaseEffectOptions } from './GSplatRevealShowcaseEffect'
import { GSplatRevealShowcaseEffect } from './GSplatRevealShowcaseEffect'
import { SHOWCASE_SHADER_COMMON_GLSL, SHOWCASE_SHADER_COMMON_WGSL } from './showcaseShaderUtils'

const DEFAULT_MAGIC_MIN_PIXEL_SIZE = 0.5

export interface GSplatRevealMagicEffectOptions extends GSplatRevealShowcaseEffectOptions {
  revealRadius?: number
  effectScale?: number
  minPixelSize?: number
}

const magicShaderGLSL = /* glsl */ `
uniform float uTime;
uniform float uTimeScale;
uniform float uMotionScale;
uniform float uDuration;
uniform float uRevealRadius;
uniform float uEffectScale;
uniform vec3 uCenter;

${SHOWCASE_SHADER_COMMON_GLSL}

vec3 revealMagicHash(vec3 value) {
  value = fract(value * 0.3183099 + 0.1);
  value *= 17.0;
  return fract(vec3(
    value.x * value.y * value.z,
    value.x + value.y * value.z,
    value.x * value.y + value.z
  ));
}

vec3 revealMagicNoise(vec3 value) {
  vec3 cell = floor(value);
  vec3 local = fract(value);
  local = local * local * (3.0 - 2.0 * local);

  vec3 n000 = revealMagicHash(cell + vec3(0.0, 0.0, 0.0));
  vec3 n100 = revealMagicHash(cell + vec3(1.0, 0.0, 0.0));
  vec3 n010 = revealMagicHash(cell + vec3(0.0, 1.0, 0.0));
  vec3 n110 = revealMagicHash(cell + vec3(1.0, 1.0, 0.0));
  vec3 n001 = revealMagicHash(cell + vec3(0.0, 0.0, 1.0));
  vec3 n101 = revealMagicHash(cell + vec3(1.0, 0.0, 1.0));
  vec3 n011 = revealMagicHash(cell + vec3(0.0, 1.0, 1.0));
  vec3 n111 = revealMagicHash(cell + vec3(1.0, 1.0, 1.0));

  vec3 x0 = mix(n000, n100, local.x);
  vec3 x1 = mix(n010, n110, local.x);
  vec3 x2 = mix(n001, n101, local.x);
  vec3 x3 = mix(n011, n111, local.x);
  vec3 y0 = mix(x0, x1, local.y);
  vec3 y1 = mix(x2, x3, local.y);

  return mix(y0, y1, local.z);
}

vec3 revealMagicToSourceSpace(vec3 local) {
  return vec3(local.x, local.z, local.y);
}

vec3 revealMagicFromSourceSpace(vec3 local) {
  return vec3(local.x, local.z, local.y);
}

float revealMagicSourceTime() {
  return min(uTime * uTimeScale * 14.5 / max(uDuration, 0.001), 14.5);
}

float revealMagicFront(float t) {
  return smoothstep(0.0, 10.0, t - 4.5) * uRevealRadius;
}

float revealMagicSpatialScale() {
  return max(uEffectScale, 0.001);
}

void modifySplatCenter(inout vec3 center) {
  vec3 local = center - uCenter;
  vec3 sourceLocal = revealMagicToSourceSpace(local);
  float t = revealMagicSourceTime();
  float front = revealMagicFront(t);
  float radius = length(local.xy);
  float spatialScale = revealMagicSpatialScale();
  float band = 0.5 * spatialScale;
  float border = abs(front - radius - band) / spatialScale;
  float hidden = smoothstep(front - band, front, radius + band);
  float motion = max(uMotionScale, 0.0);

  sourceLocal *= 1.0 - 0.2 * exp(-20.0 * border) * motion;
  sourceLocal += 0.1 * spatialScale
    * revealMagicNoise(sourceLocal * 2.0 / spatialScale + t * 0.5)
    * hidden
    * motion;

  center = uCenter + revealMagicFromSourceSpace(sourceLocal);
}

void modifySplatCovariance(
  vec3 originalCenter,
  vec3 modifiedCenter,
  inout vec3 covA,
  inout vec3 covB
) {
  vec3 local = originalCenter - uCenter;
  float t = revealMagicSourceTime();
  float front = revealMagicFront(t);
  float radius = length(local.xy);
  float spatialScale = revealMagicSpatialScale();
  float band = 0.5 * spatialScale;
  float hidden = smoothstep(front - band, front, radius + band)
    * showcaseSaturate(uMotionScale);
  vec3 tinyA = covA;
  vec3 tinyB = covB;

  gsplatMakeRound(tinyA, tinyB, 0.002 * spatialScale);
  covA = mix(covA, tinyA, hidden);
  covB = mix(covB, tinyB, hidden);
}

void modifySplatColor(vec3 center, inout vec4 color) {
  vec3 local = center - uCenter;
  float t = revealMagicSourceTime();
  float front = revealMagicFront(t);
  float radius = length(local.xy);
  float spatialScale = revealMagicSpatialScale();
  float band = 0.5 * spatialScale;
  float border = abs(front - radius - band) / spatialScale;
  float angle = atan(local.x, local.y) / 3.1416;
  float angularReveal = step(angle, t - 3.1416);
  float edgeGlow = exp(-20.0 * border);
  float sweepGlow = exp(-50.0 * abs(t - angle - 3.1416)) * 0.5;
  float glow = edgeGlow + sweepGlow;

  color.rgb = color.rgb * angularReveal + vec3(glow);
  color.a = min(1.0, color.a * angularReveal + glow);
}
`

const magicShaderWGSL = /* wgsl */ `
${SHOWCASE_SHADER_COMMON_WGSL}

fn revealMagicHash(value: vec3f) -> vec3f {
  var v = fract(value * 0.3183099 + 0.1);
  v = v * 17.0;
  return fract(vec3f(
    v.x * v.y * v.z,
    v.x + v.y * v.z,
    v.x * v.y + v.z
  ));
}

fn revealMagicNoise(value: vec3f) -> vec3f {
  let cell = floor(value);
  var local = fract(value);
  local = local * local * (vec3f(3.0) - 2.0 * local);
  let n000 = revealMagicHash(cell);
  let n100 = revealMagicHash(cell + vec3f(1.0, 0.0, 0.0));
  let n010 = revealMagicHash(cell + vec3f(0.0, 1.0, 0.0));
  let n110 = revealMagicHash(cell + vec3f(1.0, 1.0, 0.0));
  let n001 = revealMagicHash(cell + vec3f(0.0, 0.0, 1.0));
  let n101 = revealMagicHash(cell + vec3f(1.0, 0.0, 1.0));
  let n011 = revealMagicHash(cell + vec3f(0.0, 1.0, 1.0));
  let n111 = revealMagicHash(cell + vec3f(1.0, 1.0, 1.0));
  let x0 = mix(n000, n100, local.x);
  let x1 = mix(n010, n110, local.x);
  let x2 = mix(n001, n101, local.x);
  let x3 = mix(n011, n111, local.x);
  let y0 = mix(x0, x1, local.y);
  let y1 = mix(x2, x3, local.y);
  return mix(y0, y1, local.z);
}

fn revealMagicSourceTime() -> f32 {
  return min(uniform.uTime * uniform.uTimeScale * 14.5 / max(uniform.uDuration, 0.001), 14.5);
}

fn revealMagicFront(t: f32) -> f32 {
  return smoothstep(0.0, 10.0, t - 4.5) * uniform.uRevealRadius;
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let local = *center - uniform.uCenter;
  var sourceLocal = vec3f(local.x, local.z, local.y);
  let t = revealMagicSourceTime();
  let front = revealMagicFront(t);
  let radius = length(local.xy);
  let spatialScale = max(uniform.uEffectScale, 0.001);
  let band = 0.5 * spatialScale;
  let border = abs(front - radius - band) / spatialScale;
  let hidden = smoothstep(front - band, front, radius + band);
  let motion = max(uniform.uMotionScale, 0.0);
  sourceLocal = sourceLocal * (1.0 - 0.2 * exp(-20.0 * border) * motion);
  sourceLocal = sourceLocal +
    0.1 * spatialScale *
    revealMagicNoise(sourceLocal * 2.0 / spatialScale + vec3f(t * 0.5)) *
    hidden * motion;
  let restored = vec3f(sourceLocal.x, sourceLocal.z, sourceLocal.y);
  *center = uniform.uCenter + restored;
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  let local = originalCenter - uniform.uCenter;
  let t = revealMagicSourceTime();
  let front = revealMagicFront(t);
  let radius = length(local.xy);
  let spatialScale = max(uniform.uEffectScale, 0.001);
  let band = 0.5 * spatialScale;
  let hidden = smoothstep(front - band, front, radius + band) * showcaseSaturate(uniform.uMotionScale);
  let tinyScale = vec3f(0.002 * spatialScale);
  *scale = mix(*scale, tinyScale, hidden);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let local = center - uniform.uCenter;
  let t = revealMagicSourceTime();
  let front = revealMagicFront(t);
  let radius = length(local.xy);
  let spatialScale = max(uniform.uEffectScale, 0.001);
  let band = 0.5 * spatialScale;
  let border = abs(front - radius - band) / spatialScale;
  let angle = atan2(local.x, local.y) / 3.1416;
  let angularReveal = step(angle, t - 3.1416);
  let edgeGlow = exp(-20.0 * border);
  let sweepGlow = exp(-50.0 * abs(t - angle - 3.1416)) * 0.5;
  let glow = edgeGlow + sweepGlow;
  (*color) = vec4f(
    (*color).rgb * angularReveal + vec3f(glow),
    min(1.0, (*color).a * angularReveal + glow)
  );
}
`

export class GSplatRevealMagicEffect extends GSplatRevealShowcaseEffect {
  private readonly _revealRadius: number
  private readonly _effectScale: number
  private readonly _minPixelSize: number

  constructor(options: GSplatRevealMagicEffectOptions = {}) {
    super(
      {
        id: 'reveal-magic',
        shader: magicShaderGLSL,
        shaderWGSL: magicShaderWGSL,
      },
      options
    )

    this._revealRadius = normalizeRevealRadius(options.revealRadius)
    this._effectScale = normalizeEffectScale(options.effectScale)
    this._minPixelSize = options.minPixelSize ?? DEFAULT_MAGIC_MIN_PIXEL_SIZE
    this.uniforms.minPixelSize = { value: this._minPixelSize }
  }

  protected override updateEffect(effectTime: number): void {
    super.updateEffect(effectTime)
    this.setUniform('uRevealRadius', this._revealRadius)
    this.setUniform('uEffectScale', this._effectScale)
    this.setUniform('minPixelSize', this._minPixelSize)
  }
}

function normalizeRevealRadius(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.max(value, 0.001)
    : 10
}

function normalizeEffectScale(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.min(1, Math.max(value, 0.001))
    : 1
}
